import { DocPayload, EditableDocPayload } from "~/document";
import { observer } from "mobx-react";
import paths from "~/paths";
import { useState, useEffect, useMemo, useContext } from "react";
import FormGroup from "~/components/FormGroup";
import Modal from "~/components/Modal";
import { Button } from "~/components/ui/button";
import { ColumnDef, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import templatesStore, { TemplateInfo } from "~/stores/templates";
import DocumentForm, { SingleFormValue, SingleOnChangeKeys } from "~/components/DocumentForm";
import { docPayloadFromFormChange, formValueFromState } from "../UploadPage";
import BouncedTextInput from "~/components/BouncedInput";
import notificationStore from "~/stores/notifications";
import { assert, formatDateReadable } from "~/util";
import languageStore from "~/stores/language";
import TabTable, { CommonTabProps } from "./TabTable";
import TabContent from "./TabContent";
import { useDataFetcher } from "./common";
import { ToastNotificationContext } from "~/components/ToastNotificationContext";

const AddTemplateModal = (props: {
  close: () => void;
  open: boolean;
  onAdd: (name: string, payload: DocPayload) => Promise<void>;
  initialPayload: TemplateInfo | null;
}) => {
  const [templateName, setTemplateName] = useState<string>("");
  const [docPayload, setDocPayload] = useState<EditableDocPayload>({});
  // load initial template state if editing
  useEffect(() => {
    if (props.initialPayload === null) return;
    setTemplateName(props.initialPayload.description);
    setDocPayload(props.initialPayload.json_template);
  }, [props.initialPayload]);
  const formValue: SingleFormValue = useMemo(() => formValueFromState(docPayload), [docPayload]);
  const onDocChange = (key: SingleOnChangeKeys, value: any) => {
    setDocPayload((prev) => {
      return docPayloadFromFormChange(prev, key as keyof SingleFormValue, value);
    });
  };
  const formProps = {};
  const nameValid = templateName.trim().length !== 0;
  const isTemplateEmpty = Object.keys(docPayload).length === 0;
  const canSubmit = nameValid && !isTemplateEmpty;
  return (
    <Modal
      open={props.open}
      modalHeading="Create a new template"
      modalLabel="Templates"
      onRequestClose={props.close}
      onRequestSubmit={async () => {
        await props.onAdd(templateName, docPayload);
        setDocPayload({});
        setTemplateName("");
        props.close();
      }}
      primaryButtonDisabled={!canSubmit}
      primaryButtonText="Confirm"
      secondaryButtonText="Cancel"
      className="sm:max-w-[900px]"
    >
      <div className="pb-4">
        <FormGroup
          legendText="Template name"
          invalid={!nameValid}
          invalidText={!nameValid ? "Template name is required" : undefined}
        >
          <BouncedTextInput
            placeholder="e.g. Country reports fields"
            value={templateName}
            onChange={(value: any) => setTemplateName(value)}
            aria-invalid={!nameValid}
          />
        </FormGroup>
      </div>
      <DocumentForm
        value={formValue}
        onChange={onDocChange}
        multipleDocs={false}
        formProps={formProps}
        docTypeSelectProps={{
          maxOptionsToSelect: 2,
        }}
      />
    </Modal>
  );
};

const TemplatesTab = observer((props: CommonTabProps) => {
  const { setNotificationData, showNotificationWithTimeout } = useContext(ToastNotificationContext);
  const fetcher = useDataFetcher(props.selected, templatesStore.f.loadBare);
  const items = fetcher.items.results;
  const data = useMemo(() => items, [items]);
  const columns: ColumnDef<TemplateInfo, any>[] = useMemo(
    () => [
      { header: "Name", accessorKey: "description" },
      {
        id: "symbol",
        header: "Symbol",
        cell: (info) => <div className="space-x-1.5">{info.row.original.json_template.protocol ?? "N/A"}</div>,
      },
      {
        id: "language",
        header: "Language",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.json_template.language ? languageStore.idToText(doc.json_template.language) : "N/A"}
            </div>
          );
        },
      },
      {
        id: "release_date",
        header: "Release date",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.json_template.release_date ? formatDateReadable(doc.json_template.release_date) : "N/A"}
            </div>
          );
        },
      },
      {
        id: "expires_at",
        header: "Expires at",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-1.5">
              {doc.json_template.expiration_date ? formatDateReadable(doc.json_template.expiration_date) : "N/A"}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: (info) => {
          const doc = info.row.original;
          return (
            <div className="space-x-3">
              <Link className="text-primary-600 hover:underline" to={paths.construct.uploadDocWithTemplate(doc.id)}>
                Upload
              </Link>
              <Link
                className="text-primary-600 hover:underline"
                to="#"
                onClick={async (e) => {
                  e.preventDefault();
                  const template = await templatesStore.getTemplate(doc.id);
                  setInitialTemplatePayload(template);
                  setShowTemplateModal(true);
                }}
              >
                Edit
              </Link>
              <Link
                className="text-primary-600 hover:underline"
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setTemplateToDelete(doc);
                }}
              >
                Delete
              </Link>
            </div>
          );
        },
      },
    ],
    []
  );
  const tableInstance = useReactTable({
    data,
    columns,
    enableSorting: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const [showTemplateModal, setShowTemplateModal] = useState(false);

  // stores edited payload
  const [initialTemplatePayload, setInitialTemplatePayload] = useState<TemplateInfo | null>(null);
  const editing = initialTemplatePayload !== null;

  const notify = (data: { kind: "success" | "error"; title: string }) => {
    setNotificationData(data);
    showNotificationWithTimeout();
  };

  const onAdd = async (templateName: string, docPayload: DocPayload) => {
    if (editing) {
      const result = await templatesStore.editTemplate(initialTemplatePayload.id, templateName, docPayload);
      if (result === null) {
        notify({ kind: "error", title: "Template was not edited due to an API error" });
        return;
      }
      await fetcher.reload();
      notify({ kind: "success", title: "Template edited successfully" });
    } else {
      const result = await templatesStore.addTemplate(templateName, docPayload);
      if (result === null) {
        notify({ kind: "error", title: "Template was not created due to an API error" });
        return;
      }
      await fetcher.reload();
      notify({ kind: "success", title: "Template created successfully" });
    }
  };
  const [templateToDelete, setTemplateToDelete] = useState<TemplateInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const templateDeletionModalShown = templateToDelete !== null;

  return (
    <TabContent>
      <Modal
        open={templateDeletionModalShown}
        modalHeading="Delete template"
        modalLabel="Templates"
        onRequestClose={() => setTemplateToDelete(null)}
        primaryButtonText="Confirm"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={loading}
        onRequestSubmit={async () => {
          setLoading(true);
          assert(templateToDelete !== null, "Template should already be selected when deletion modal is open");
          await templatesStore.f.delete(templateToDelete.id);
          notificationStore.success("Deleted template");
          await fetcher.reload();
          setTemplateToDelete(null);
          setLoading(false);
        }}
      >
        Do you really want to delete the template "{templateToDelete?.description}"?
      </Modal>
      <AddTemplateModal
        open={showTemplateModal}
        close={() => setShowTemplateModal(false)}
        onAdd={onAdd}
        initialPayload={initialTemplatePayload}
      />
      <div className="flex mb-4">
        <div className="flex-[1_1_100%]" />
        <Button variant="default" onClick={() => setShowTemplateModal(true)}>
          Add template
        </Button>
      </div>
      <TabTable
        fetcher={fetcher}
        tableInstance={tableInstance}
        emptyTitle="No templates"
        emptyMessage="You haven't created any templates"
      />
    </TabContent>
  );
});

export default TemplatesTab;
